import React, { useState } from 'react';
import { 
  Button, 
  Modal, 
  Form, 
  TimePicker, 
  Space, 
  Row,
  Col,
  Typography,
  Select,
  DatePicker,
  Switch,
  Input,
  Card,
  InputNumber,
  Tooltip
} from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import apiService from '../../../services/api';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import { 
  MentorAvailabilityResponse, 
  CreateMentorAvailabilityRequest, 
  UpdateMentorAvailabilityRequest,
  BulkCreateAvailabilityRequest
} from '../../../types';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const AvailabilityTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [modalVisible, setModalVisible] = useState(false);
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState<MentorAvailabilityResponse | null>(null);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
  const [modalSelectedDate, setModalSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [form] = Form.useForm();
  const [bulkForm] = Form.useForm();
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyWeeks, setCopyWeeks] = useState<number>(1);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const daysOfWeek = [
    { value: 'Monday', label: 'Pazartesi' },
    { value: 'Tuesday', label: 'Salı' },
    { value: 'Wednesday', label: 'Çarşamba' },
    { value: 'Thursday', label: 'Perşembe' },
    { value: 'Friday', label: 'Cuma' },
    { value: 'Saturday', label: 'Cumartesi' },
    { value: 'Sunday', label: 'Pazar' }
  ];

  const getMondayOfWeek = (d: dayjs.Dayjs) => {
    const day = d.day();
    return day === 0 ? d.subtract(6, 'day') : d.subtract(day - 1, 'day');
  };

  const weekStart = getMondayOfWeek(selectedDate);
  const weekDays: dayjs.Dayjs[] = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));

  const hours = Array.from({ length: 16 }, (_, i) => 8 + i);

  const weekLabel = `${weekStart.format('D MMMM YYYY dddd')} - ${weekStart.add(6,'day').format('D MMMM YYYY dddd')}`;

  const { data: availabilities = [], isLoading: loading } = useQuery({
    queryKey: mentorProfileQueryKeys.availability,
    queryFn: async () => {
      const res = await apiService.getMentorAvailabilities();
      if (!res.success) throw new Error(res.message || 'Müsaitlikler yüklenemedi');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateMentorAvailabilityRequest) => apiService.createMentorAvailability(data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Müsaitlik başarıyla eklendi');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.availability });
        setModalVisible(false);
      } else toast.error(res.message || 'Müsaitlik eklenemedi');
    },
    onError: () => toast.error('Müsaitlik eklenirken bir hata oluştu'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMentorAvailabilityRequest }) =>
      apiService.updateMentorAvailability(id, data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Müsaitlik başarıyla güncellendi');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.availability });
        setModalVisible(false);
      } else toast.error(res.message || 'Müsaitlik güncellenemedi');
    },
    onError: () => toast.error('Müsaitlik güncellenirken bir hata oluştu'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteMentorAvailability(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Müsaitlik başarıyla silindi');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.availability });
        setDeleteModalVisible(false);
        setDeletingId(null);
        setModalVisible(false);
      } else toast.error(res.message || 'Müsaitlik silinemedi');
    },
    onError: () => toast.error('Müsaitlik silinirken bir hata oluştu'),
  });

  const bulkCreateMutation = useMutation({
    mutationFn: (data: BulkCreateAvailabilityRequest) => apiService.bulkCreateAvailability(data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success(`${res.data.length} müsaitlik başarıyla eklendi`);
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.availability });
        setBulkModalVisible(false);
      } else toast.error(res.message || 'Toplu müsaitlik eklenemedi');
    },
    onError: () => toast.error('Toplu müsaitlik eklenirken bir hata oluştu'),
  });

  const handleAdd = () => {
    setEditingAvailability(null);
    setModalSelectedDate(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleBulkAdd = () => {
    bulkForm.resetFields();
    setBulkModalVisible(true);
  };

  const handleEdit = (availability: MentorAvailabilityResponse) => {
    setEditingAvailability(availability);
    setModalSelectedDate(dayjs(availability.startTime));
    form.setFieldsValue({
      startTime: dayjs(availability.startTime),
      endTime: dayjs(availability.endTime),
      notes: availability.notes,
      isAvailable: availability.isAvailable
    });
    setModalVisible(true);
  };

  const handleDelete = (id: number) => deleteMutation.mutate(id);

  const handleDeleteClick = (id: number) => {
    setDeletingId(id);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (deletingId) handleDelete(deletingId);
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeletingId(null);
  };

  const handleCellClick = (d: dayjs.Dayjs, h: number) => {
    const cellSlots = availabilities.filter(a => dayjs(a.startTime).isSame(d,'day') && dayjs(a.startTime).hour()===h);
    
    if (cellSlots.length > 0) {
      // Eğer hücrede slot varsa, ilk slotu düzenleme için aç
      // Eğer birden fazla slot varsa (duplicate durumu), ilkini göster
      handleEdit(cellSlots[0]);
    } else {
      // Eğer hücrede slot yoksa, yeni slot ekle
      setSelectedDate(d);
      setModalSelectedDate(d);
      
      // Gün adını Türkçe'ye çevir
      const dayMap: { [key: string]: string } = {
        'Monday': 'Monday',
        'Tuesday': 'Tuesday', 
        'Wednesday': 'Wednesday',
        'Thursday': 'Thursday',
        'Friday': 'Friday',
        'Saturday': 'Saturday',
        'Sunday': 'Sunday'
      };
      
      const englishDay = d.format('dddd');
      const turkishDay = dayMap[englishDay] || englishDay;
      
      const startTime = d.hour(h).minute(0).second(0);
      const endTime = d.hour(h+1).minute(0).second(0);
      
      form.setFieldsValue({
        startTime: startTime,
        endTime: endTime,
        isAvailable: true,
        notes: undefined
      });
      setEditingAvailability(null);
      setModalVisible(true);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      // Tarihleri doğru şekilde hesapla
      const start = values.startTime;
      const end = values.endTime;

      if (!end.isAfter(start)) {
        toast.error('Bitiş saati başlangıç saatinden büyük olmalıdır');
        return;
      }
      
      // Seçilen tarihten gün adını al
      const selectedDate = form.getFieldValue('startTime');
      const dayOfWeek = selectedDate ? selectedDate.format('dddd') : dayjs().format('dddd');
      
      const availabilityData: CreateMentorAvailabilityRequest | UpdateMentorAvailabilityRequest = {
        startTime: start.format('YYYY-MM-DDTHH:mm:ss'),
        endTime: end.format('YYYY-MM-DDTHH:mm:ss'),
        dayOfWeek: dayOfWeek,
        notes: values.notes,
        isAvailable: values.isAvailable
      };

      if (editingAvailability) {
        updateMutation.mutate({ id: editingAvailability.id, data: availabilityData });
      } else {
        const sameTimeSlot = availabilities.find(a => {
          const aStart = dayjs(a.startTime);
          return aStart.isSame(start, 'day') && 
                 aStart.hour() === start.hour() && 
                 aStart.minute() === start.minute();
        });
        if (sameTimeSlot) {
          toast.warning('Bu saat dilimi için zaten bir müsaitlik kaydı bulunmaktadır. Lütfen mevcut kaydı düzenleyin veya silin.');
          return;
        }
        createMutation.mutate(availabilityData);
      }
    } catch (error) {
      console.error('Form doğrulama hatası:', error);
    }
  };

  const handleBulkModalOk = async () => {
    try {
      const values = await bulkForm.validateFields();
      const bulkData: BulkCreateAvailabilityRequest = {
        startDate: values.dateRange[0].format('YYYY-MM-DD'),
        endDate: values.dateRange[1].format('YYYY-MM-DD'),
        startTime: values.startTime.format('HH:mm:ss'),
        endTime: values.endTime.format('HH:mm:ss'),
        daysOfWeek: values.daysOfWeek,
        notes: values.notes,
        isAvailable: values.isAvailable
      };
      bulkCreateMutation.mutate(bulkData);
    } catch {
      // form validation
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingAvailability(null);
    setModalSelectedDate(null);
    form.resetFields();
  };

  const handleBulkModalCancel = () => {
    setBulkModalVisible(false);
    bulkForm.resetFields();
  };

  // Get availabilities for selected date
  const getSelectedDateAvailabilities = () => {
    const dayOfWeek = selectedDate.format('dddd');
    return availabilities.filter(a => 
      a.dayOfWeek === dayOfWeek && 
      dayjs(a.startTime).isSame(selectedDate, 'day')
    );
  };

  return (
    <PanelTabCard
      title="Müsaitlik Takvimi"
      description="Haftalık müsaitlik saatlerinizi yönetin."
    >
      <style>
        {`
          .ant-btn:not(.ant-btn-primary):hover {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
          .ant-btn:not(.ant-btn-primary):focus {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
          .ant-btn:not(.ant-btn-primary):active {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
        `}
      </style>

      <TabListLoading loading={loading}>
      <Row gutter={24}>
        <Col span={24}>
          <Card style={{ position: 'relative', paddingTop: 8 }}>
            {/* Haftalık ızgara başlık */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 12 }}>
              <Space>
                <Button size="small" onClick={() => setSelectedDate(prev=>prev.subtract(1,'week'))}>‹</Button>
                <div style={{ fontWeight: 600 }}>{weekLabel}</div>
                <Button size="small" onClick={() => setSelectedDate(prev=>prev.add(1,'week'))}>›</Button>
                <Button size="small" onClick={() => setSelectedDate(dayjs())}>Bugün</Button>
              </Space>
              <Space>
                <Button size="small" onClick={() => { setCopyWeeks(1); setCopyModalOpen(true); }}>Haftayı Kopyala</Button>
              </Space>
            </div>

            {/* Haftalık saat ızgarası */}
            <div style={{ overflowX: 'hidden', overflowY: 'auto', maxWidth: '100%' }}>
              <table style={{ width:'100%', minWidth: 0, borderCollapse:'collapse', border:'1px solid #d9d9d9', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ width:80, textAlign:'left', padding:'8px 4px', color:'var(--text-secondary)', borderRight:'1px solid #d9d9d9' }}>SAAT</th>
                    {weekDays.map(d => (
                      <th key={d.toString()} style={{ width: 'calc((100% - 80px) / 7)', padding:'8px 4px', color:'var(--text-secondary)', textAlign:'center', borderRight:'1px solid #d9d9d9' }}>
                        <div style={{ fontWeight:600 }}>{d.format('dddd').toUpperCase()}</div>
                        <div>{d.format('DD.MM')}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {hours.map(h => (
                    <tr key={h}>
                      <td style={{ width:80, height: 52, padding:'6px 6px', borderTop:'1px solid #eee', color:'#374151', borderRight:'1px solid #d9d9d9' }}>{`${String(h).padStart(2,'0')}:00`}</td>
                      {weekDays.map(d => {
                        const cellSlots = availabilities.filter(a => dayjs(a.startTime).isSame(d,'day') && dayjs(a.startTime).hour()===h);
                        return (
                          <td key={`${d.toString()}-${h}`} style={{ width: 'calc((100% - 80px) / 7)', height: 52, padding: 6, borderTop:'1px solid #eee', borderRight:'1px solid #d9d9d9', cursor: 'pointer', verticalAlign: 'top', overflow: 'hidden' }}
                            onClick={() => handleCellClick(d, h)}
                          >
                            <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {cellSlots.map(s => {
                                const timeStr = `${dayjs(s.startTime).format('HH:mm')}-${dayjs(s.endTime).format('HH:mm')}`;
                                const label = s.isAvailable ? 'Müsait' : 'Müsait değil';
                                const hasNotes = s.notes?.trim();
                                const titleContent = hasNotes
                                  ? `${label} • ${timeStr}\n${s.notes!.trim()}`
                                  : `${label} • ${timeStr}`;
                                return (
                                  <Tooltip
                                    key={s.id}
                                    title={<span style={{ whiteSpace: 'pre-wrap' }}>{titleContent}</span>}
                                    trigger={['hover', 'click']}
                                  >
                                    <div
                                      role="button"
                                      tabIndex={0}
                                      onClick={(e) => { e.stopPropagation(); handleEdit(s); }}
                                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEdit(s); } }}
                                      style={{
                                        background: s.isAvailable ? '#e6f4ea' : '#fee2e2',
                                        border: `1px solid ${s.isAvailable ? '#b7eb8f' : '#fecaca'}`,
                                        color: s.isAvailable ? '#166534' : '#991b1b',
                                        borderRadius: 4,
                                        padding: '5px 8px',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        cursor: 'pointer',
                                        flexShrink: 0
                                      }}
                                    >
                                      {hasNotes ? `${timeStr} …` : timeStr}
                                    </div>
                                  </Tooltip>
                                );
                              })}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>
      </TabListLoading>

      {/* Single Availability Modal */}
      <Modal
        title={
          <div>
            {editingAvailability ? 'Müsaitlik Düzenle' : 'Yeni Müsaitlik'}
            {modalSelectedDate && (
              <div style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginTop: '4px' }}>
                {modalSelectedDate.format('D MMMM dddd YYYY')}
              </div>
            )}
          </div>
        }
        open={modalVisible}
        onCancel={handleModalCancel}
        width={500}
        footer={[
          <Button key="cancel" onClick={handleModalCancel}>
            İptal
          </Button>,
          editingAvailability && (
            <Button key="delete" danger onClick={() => {
              if (editingAvailability?.id) {
                handleDeleteClick(editingAvailability.id);
              }
            }}>
              Sil
            </Button>
          ),
          <Button key="save" type="primary" onClick={handleModalOk} style={{
            backgroundColor: 'var(--text-secondary)',
            borderColor: 'var(--text-secondary)'
          }}>
            Kaydet
          </Button>
        ].filter(Boolean)}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startTime"
                label="Başlangıç Saati"
                rules={[{ required: true, message: 'Başlangıç saati gereklidir' }]}
                extra="Sadece tam saatler seçilebilir (örn: 19:00, 20:00)"
              >
                <TimePicker
                  style={{ width: '100%' }}
                  format="HH:mm"
                  disabledMinutes={() => Array.from({length:60}, (_,m)=>m).filter(m => m !== 0)}
                  disabledHours={() => Array.from({length:24}, (_,h)=>h).filter(h => h < 8 || h > 22)}
                  onChange={(time) => {
                    if (time) {
                      const endTime = time.add(1, 'hour');
                      form.setFieldsValue({ endTime });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endTime"
                label="Bitiş Saati"
                rules={[{ required: true, message: 'Bitiş saati gereklidir' }]}
                extra="Otomatik olarak 1 saat sonrasına ayarlanır"
              >
                <TimePicker
                  style={{ width: '100%' }}
                  format="HH:mm"
                  disabledMinutes={() => Array.from({length:60}, (_,m)=>m).filter(m => m !== 0)}
                  disabledHours={() => Array.from({length:24}, (_,h)=>h).filter(h => h < 8 || h > 23)}
                  disabled={true}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isAvailable"
            label="Müsaitlik Durumu"
            valuePropName="checked"
          >
            <Switch checkedChildren="Müsait" unCheckedChildren="Müsait Değil" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notlar"
          >
            <TextArea rows={3} placeholder="Özel notlar" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk Availability Modal */}
      <Modal
        title="Toplu Müsaitlik Ekle"
        open={bulkModalVisible}
        onOk={handleBulkModalOk}
        onCancel={handleBulkModalCancel}
        width={600}
        okText="Oluştur"
        cancelText="İptal"
        okButtonProps={{
          style: {
            backgroundColor: '#10b981',
            borderColor: '#10b981',
            color: 'white',
            fontWeight: 500
          }
        }}
        cancelButtonProps={{
          style: {
            backgroundColor: 'white',
            borderColor: '#d1d5db',
            color: 'var(--text-secondary)',
            fontWeight: 500
          }
        }}
      >
        <Form
          form={bulkForm}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="dateRange"
            label="Tarih Aralığı"
            rules={[{ required: true, message: 'Tarih aralığı gereklidir' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="daysOfWeek"
            label="Haftanın Günleri"
            rules={[{ required: true, message: 'En az bir gün seçmelisiniz' }]}
          >
            <Select mode="multiple" placeholder="Günleri seçin">
              {daysOfWeek.map(day => (
                <Option key={day.value} value={day.value}>
                  {day.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startTime"
                label="Başlangıç Saati"
                rules={[{ required: true, message: 'Başlangıç saati gereklidir' }]}
                extra="Sadece tam saatler seçilebilir (örn: 19:00, 20:00)"
              >
                <TimePicker
                  style={{ width: '100%' }}
                  format="HH:mm"
                  disabledMinutes={() => Array.from({length:60}, (_,m)=>m).filter(m => m !== 0)}
                  disabledHours={() => Array.from({length:24}, (_,h)=>h).filter(h => h < 8 || h > 22)}
                  onChange={(time) => {
                    if (time) {
                      const endTime = time.add(1, 'hour');
                      bulkForm.setFieldsValue({ endTime });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endTime"
                label="Bitiş Saati"
                rules={[{ required: true, message: 'Bitiş saati gereklidir' }]}
                extra="Otomatik olarak 1 saat sonrasına ayarlanır"
              >
                <TimePicker
                  style={{ width: '100%' }}
                  format="HH:mm"
                  disabledMinutes={() => Array.from({length:60}, (_,m)=>m).filter(m => m !== 0)}
                  disabledHours={() => Array.from({length:24}, (_,h)=>h).filter(h => h < 8 || h > 23)}
                  disabled={true}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isAvailable"
            label="Müsaitlik Durumu"
            valuePropName="checked"
          >
            <Switch checkedChildren="Müsait" unCheckedChildren="Müsait Değil" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notlar"
          >
            <TextArea rows={3} placeholder="Tüm müsaitlikler için geçerli notlar" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Copy Week Modal */}
      <Modal
        title="Haftayı Kopyala"
        open={copyModalOpen}
        onOk={async () => {
          try {
            const monday = getMondayOfWeek(selectedDate);
            const endOfWeek = monday.add(6,'day');
            const weekSlots = availabilities.filter(a => dayjs(a.startTime).isAfter(monday.startOf('day').subtract(1,'second')) && dayjs(a.startTime).isBefore(endOfWeek.endOf('day').add(1,'second')));
            let createdCount = 0;
            let skippedCount = 0;
            for (let w=1; w<=copyWeeks; w++) {
              for (const s of weekSlots) {
                const start = dayjs(s.startTime).add(w,'week');
                const end = dayjs(s.endTime).add(w,'week');
                const existingSlot = availabilities.find(a => {
                  const aStart = dayjs(a.startTime);
                  return aStart.isSame(start, 'day') && aStart.hour() === start.hour() && aStart.minute() === start.minute();
                });
                if (existingSlot) {
                  skippedCount++;
                  continue;
                }
                const res = await apiService.createMentorAvailability({
                  startTime: start.format('YYYY-MM-DDTHH:mm:ss'),
                  endTime: end.format('YYYY-MM-DDTHH:mm:ss'),
                  dayOfWeek: start.format('dddd'),
                  notes: s.notes,
                  isAvailable: s.isAvailable
                } as CreateMentorAvailabilityRequest);
                if (res.success) createdCount++;
              }
            }
            if (skippedCount > 0) {
              toast.warning(`${createdCount} müsaitlik oluşturuldu, ${skippedCount} müsaitlik zaten mevcut olduğu için atlandı.`);
            } else {
              toast.success(`${createdCount} müsaitlik başarıyla kopyalandı`);
            }
            setCopyModalOpen(false);
            queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.availability });
          } catch (e) {
            console.error('Kopyalama hatası:', e);
            toast.error('Kopyalama sırasında hata oluştu');
          }
        }}
        onCancel={() => setCopyModalOpen(false)}
        okText="Kopyala"
        cancelText="İptal"
      >
        <Space direction="vertical" style={{ width:'100%' }}>
          <div>Seçili haftayı kaç hafta ileri kopyalamak istersiniz?</div>
          <InputNumber min={1} max={52} value={copyWeeks} onChange={(v: number | null)=> setCopyWeeks((v ?? 1))} />
        </Space>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Müsaitliği Sil"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Sil"
        cancelText="İptal"
        okButtonProps={{
          danger: true,
          style: {
            backgroundColor: '#dc2626',
            borderColor: '#dc2626',
            color: 'white',
            fontWeight: 500
          }
        }}
        cancelButtonProps={{
          style: {
            backgroundColor: 'white',
            borderColor: '#d1d5db',
            color: 'var(--text-secondary)',
            fontWeight: 500
          }
        }}
      >
        <p>Bu müsaitliği silmek istediğinizden emin misiniz?</p>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
          Bu işlem geri alınamaz.
        </p>
      </Modal>
    </PanelTabCard>
  );
};

export default AvailabilityTab;

